import React from "react";
import { Trash2 } from "lucide-react";
import { ClaimNonOrderProductColumnHeader } from "src/enums";
import { ClaimNonOrderProductInputField, TableColumn } from "src/types";
import { TextInputBox, TextInputBoxLarge } from "src/components/ui/inputs";
import { Table } from "src/components";

interface ClaimNonOrderProductInputTableProps {
  nonOrderProducts: ClaimNonOrderProductInputField[];
  setNonOrderProducts: React.Dispatch<React.SetStateAction<ClaimNonOrderProductInputField[]>>;
  minNonOrderProducts?: number;
  getProductError?: (
    productIndex: number,
    fieldName: "productId" | "claimQuantity" | "extraInfo"
  ) => string | undefined;
  generalError?: string;
}

const ClaimNonOrderProductInputTable: React.FC<ClaimNonOrderProductInputTableProps> = ({
  nonOrderProducts,
  setNonOrderProducts,
  minNonOrderProducts = 0,
  getProductError,
  generalError
}) => {
  const handleProductInputChange = <T,>(id: string, fieldName: keyof ClaimNonOrderProductInputField, value: T) => {
    setNonOrderProducts(
      nonOrderProducts.map((product) => (product.fieldId === id ? { ...product, [fieldName]: value } : product))
    );
  };

  const ClaimNonOrderProductInputTableColumns: TableColumn<
    ClaimNonOrderProductColumnHeader,
    ClaimNonOrderProductInputField
  >[] = [
    {
      header: ClaimNonOrderProductColumnHeader.PRODUCT_ID,
      column: (row: ClaimNonOrderProductInputField) => {
        const productIndex = nonOrderProducts.findIndex((p) => p.fieldId === row.fieldId);
        const error = getProductError ? getProductError(productIndex, "productId") : undefined;
        return (
          <div className="w-48">
            <TextInputBox
              id={`non-order-product-id-${row.fieldId}`}
              value={row.productId ?? ""}
              onChange={(value) => handleProductInputChange(row.fieldId, "productId", value)}
              maxLength={100}
              placeholder="Product ID"
              size="sm"
              error={error}
            />
          </div>
        );
      },
      styles: { minWidth: "200px" }
    },
    {
      header: ClaimNonOrderProductColumnHeader.CLAIM_QUANTITY,
      column: (row: ClaimNonOrderProductInputField) => {
        const claimQuantity =
          nonOrderProducts.find((product) => product.fieldId === row.fieldId)?.claimQuantity?.toString() || "";
        const productIndex = nonOrderProducts.findIndex((p) => p.fieldId === row.fieldId);
        const error = getProductError ? getProductError(productIndex, "claimQuantity") : undefined;

        return (
          <div className="w-24">
            <TextInputBox
              id={`non-order-claim-quantity-${row.fieldId}`}
              value={claimQuantity}
              onChange={(value) => handleProductInputChange(row.fieldId, "claimQuantity", value)}
              maxLength={10}
              placeholder="Qty"
              size="sm"
              error={error}
            />
          </div>
        );
      },
      styles: { minWidth: "120px" }
    },
    {
      header: ClaimNonOrderProductColumnHeader.EXTRA_INFO,
      column: (row: ClaimNonOrderProductInputField) => {
        const extraInfo = nonOrderProducts.find((product) => product.fieldId === row.fieldId)?.extraInfo || "";

        return (
          <div className="w-full min-w-[200px]">
            <TextInputBoxLarge
              id={`non-order-extra-info-${row.fieldId}`}
              value={extraInfo}
              onChange={(value) => handleProductInputChange(row.fieldId, "extraInfo", value)}
              maxLength={1000}
              placeholder="Extra information (optional)"
              rows={2}
            />
          </div>
        );
      },
      styles: { minWidth: "250px" }
    },
    {
      header: ClaimNonOrderProductColumnHeader.PLACE_HOLDER,
      column: (row: ClaimNonOrderProductInputField) => (
        <button
          onClick={() => {
            if (nonOrderProducts.length > minNonOrderProducts) {
              setNonOrderProducts(nonOrderProducts.filter((product) => product.fieldId !== row.fieldId));
            }
          }}
          disabled={nonOrderProducts.length <= minNonOrderProducts}
          className="group flex items-center justify-center p-2 rounded-md hover:bg-error-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-transparent"
          title="Delete product"
          type="button"
        >
          <Trash2 className="w-4 h-4 text-error-600 group-hover:text-error-700 transition-colors" />
        </button>
      ),
      styles: { minWidth: "60px", textAlign: "center" }
    }
  ];

  return (
    <div>
      {generalError && (
        <div className="mb-3 p-3 bg-error-50 border border-error-200 rounded-lg">
          <p className="text-sm text-error-700">{generalError}</p>
        </div>
      )}
      <Table columns={ClaimNonOrderProductInputTableColumns} data={nonOrderProducts} />
    </div>
  );
};

export { ClaimNonOrderProductInputTable };
